import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@core/lib/utils"

export type ButtonVariant = "primary"
| "secondary"
| "secondaryOnHover"
| "info"
| "success"
| "warning"
| "danger"
| "surface"
| "outline"
| "outlinePrimary"
| "outlinePrimaryLight"
| "outlineSecondary"
| "outlineSuccess"
| "outlineDark"
| "outlineWhite"
| "outlineWarning"
| "ghost"
| "ghostWarning"
| "dark"
| "link"
| "custom";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-[var(--button-radius)] text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-neutral-950 dark:focus-visible:ring-neutral-300",
  {
    variants: {
      variant: {
        primary: "bg-primary-500 hover:bg-primary-600 dark:bg-primary-500 dark:hover:bg-primary-400 text-white",
        secondary: "bg-secondary-500 hover:bg-secondary-600 dark:bg-secondary-500 dark:hover:bg-secondary-400 text-white",
        secondaryOnHover: "bg-transparent text-neutral-900 dark:text-neutral-300 hover:bg-secondary-500 hover:text-white dark:hover:bg-secondary-400",
        info: "bg-info-500 hover:bg-info-600 dark:bg-info-500 dark:hover:bg-info-400 text-white",
        success: "bg-success-500 hover:bg-success-600 dark:bg-success-500 dark:hover:bg-success-400 text-white",
        warning: "bg-warning-500 hover:bg-warning-600 dark:bg-warning-500 dark:hover:bg-warning-400 text-white",
        danger: "bg-danger-500 hover:bg-danger-600 dark:bg-danger-500 dark:hover:bg-danger-400 text-white",
        surface: "bg-surface-500 hover:bg-surface-600 dark:bg-surface-500 dark:hover:bg-surface-400 text-white",
        outline: "bg-transparent border border-neutral-300 text-neutral-900 dark:border-neutral-700 dark:text-neutral-300",
        outlinePrimary: "bg-transparent border border-primary-500 text-primary-500 dark:border-primary-600 dark:text-primary-600",
        outlinePrimaryLight: "bg-transparent border border-primary-400 text-primary-400 dark:border-primary-400 dark:text-primary-400",
        outlineSecondary: "bg-transparent border border-secondary-500 text-secondary-500 dark:border-secondary-600 dark:text-secondary-600",
        outlineSuccess: "bg-transparent border border-success-500 text-success-500 dark:border-success-600 dark:text-success-600",
        outlineDark: "bg-white border border-primary-800 text-primary-800 dark:border-primary-900 dark:text-primary-900",
        outlineWhite: "bg-transparent border border-white-500 text-white dark:border-white-600 dark:text-white",
        outlineWarning: "bg-transparent border border-warning-500 text-warning-500 dark:border-warning-600 dark:text-warning-600",
        ghost: "bg-transparent text-neutral-900 dark:text-neutral-300",
        ghostWarning: "bg-transparent text-warning-500 dark:text-warning-600",
        dark: "bg-transparent text-surface-900 dark:text-surface-300",
        link: "bg-transparent text-primary-500 hover:text-primary-600 underline dark:text-primary-400 dark:hover:text-primary-300",
        custom: "", // No base styles - allows CustomClasses to fully control appearance
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-8 rounded-md px-2 text-xs",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type="button"
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
